/**
 * Frappe sends money as a bare number with the currency on the parent document.
 * Bosun carries { amount, currency } internally — three currencies are live per document
 * (customer / supplier / base), so a bare number is ambiguous.
 *
 * Never convert to base currency here. Every document already carries server-computed
 * base_* fields; read those. See docs/bosun-frappe-contract.md §5.1.
 */

import type { Money } from './types'

/**
 * Last-resort currency for `money()` ONLY, for the case where a document genuinely carries no
 * currency of its own. It is NOT a stand-in for the company's base currency: base currency is
 * `Company.default_currency` and every mapper that needs it takes it as a REQUIRED parameter,
 * so a forgotten argument is a compile error rather than a silent "AED".
 */
const DEFAULT_CURRENCY = 'AED'

/**
 * The one numeric coercion rule for anything off the wire. Exported so the mappers' `num`
 * helper (src/lib/api/mappers/coerce.ts) cannot disagree with money amounts about what the
 * string `"240"` means.
 */
export function toAmount(value: unknown): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0
  if (typeof value === 'string') {
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

export function money(amount: unknown, currency: unknown, fallbackCurrency = DEFAULT_CURRENCY): Money {
  const code = typeof currency === 'string' && currency !== '' ? currency : fallbackCurrency
  return { amount: toAmount(amount), currency: code }
}

/**
 * Reads the server's `base_<field>` companion — already in company currency.
 * `baseMoney(doc, 'grand_total', 'AED')` reads `doc.base_grand_total`.
 */
export function baseMoney(doc: Record<string, unknown>, field: string, baseCurrency: string): Money {
  return money(doc[`base_${field}`], baseCurrency, baseCurrency)
}
